import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { WorkspaceMember } from "@dashboard/workspaces/types";
import { DialogContentText } from "@mui/material";
import AuthService from "@services/authService";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspace } from "../../../api/helpers";
import { getWorkspaceMemberRemovalGate } from "../memberRemoval";

type Props = {
    member: WorkspaceMember;
};
export const RemoveWorkspaceMemberButton = (props: Props) => {
    const [dialogOpen, setDialogOpen] = React.useReducer((state) => !state, false);
    const [loading, setLoading] = React.useState(false);
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();

    const { t } = useTranslation(["common", "pages", "modals"]);

    const ownEmail = AuthService.getCurrentUser()?.email;

    const { data: workspace, isLoading: isWorkspaceLoading, isError: isWorkspaceError } = useCurrentWorkspace();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const { workspaceAbility } = PermissionService();
    const ability = workspaceAbility(workspace);
    const mayManageMember = ability.can("manage", "Member");
    const mayManageThisMember = ability.can("manage", memberSubject(props.member.role));

    const isWorkspaceMember = workspace?.members.some((m) => m.email === props.member.email);

    const { titleKey, mayRemove } = getWorkspaceMemberRemovalGate({
        targetRole: props.member.role,
        isSelf: props.member.email === ownEmail,
        isWorkspaceMember: !!isWorkspaceMember,
        mayManageMembers: mayManageMember,
        mayManageTargetRole: mayManageThisMember,
    });

    const onConfirmDelete = async () => {
        if (!workspaceId) {
            return;
        }

        setLoading(true);
        await updateWorkspaceMembers({
            id: workspaceId,
            removedMembers: [props.member.email],
        })
            .unwrap()
            .then(() => showSuccessToast(t("pages:workspacePage.toast.membersUpdateSuccess")))
            .finally(() => setLoading(false));
    };

    if (isWorkspaceLoading || isWorkspaceError) {
        return null;
    }

    return (
        <React.Fragment>
            <DangerOutlinedButton
                onClick={setDialogOpen}
                disabled={!mayRemove}
                tooltipProps={{ title: t(titleKey) }}
                loading={loading}
            >
                <i className="fa-solid fa-trash" />
            </DangerOutlinedButton>

            <ConfirmDialog
                title={t("modals:removeMember.title")}
                dangerButton
                primaryActionText={t("common:button.remove")}
                open={dialogOpen}
                onConfirm={onConfirmDelete}
                onClose={setDialogOpen}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.descriptionWorkspace"
                        values={{ email: props.member.email, role: props.member.role, name: workspace?.name }}
                        components={{
                            bold: <strong />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
